// Product data from the DYSSC HexCore & ProFoam content brief (TYTO Compose, DYSSC-M04-2026, V1).
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const TECH_IMG = {
  cutaway: `${API}/static/technicals/render_pair.webp`,
  renderHex: `${API}/static/technicals/render_hexcore.webp`,
  renderPro: `${API}/static/technicals/render_profoam.webp`,
  cutawayHex: `${API}/static/technicals/cutaway_hexcore.webp`,
  cutawayPro: `${API}/static/technicals/cutaway_profoam.webp`,
  coreHex: `${API}/static/technicals/core_hexcore.webp`,
  corePro: `${API}/static/technicals/core_profoam.webp`,
  paddleHex: `${API}/static/products/hexcore_n1.png`,
  paddlePro: `${API}/static/products/profoam_n1.png`,
  teardownHex: `${API}/static/technicals/teardown_b.webp`,
  teardownPro: `${API}/static/technicals/teardown_a.webp`,
};

export const PADDLES = {
  hexcore: {
    slug: 'hexcore',
    name: 'HexCore',
    fullName: 'DYSSC HexCore Pro',
    accent: '#E02128',
    identity: 'Firm, steady honeycomb feel',
    core: 'Polypropylene honeycomb',
    summary:
      'Carbon faces over a honeycomb core. HexCore feels firmer on touch shots, stays steadier on off-centre hits and bends the same way on every shot.',
    chooseFor: 'A firmer feel, more stability on off-centre hits and a solid 231.8 g build.',
    highlights: [
      { title: 'Steadier on off-centre hits', data: 'Twist weight 5.67 vs 5.49', copy: 'Resists twisting a little more when the ball lands off-centre.' },
      { title: 'Firmer on touch shots', data: 'About 37% less face bend on soft shots', copy: 'A firmer feel on dinks and resets from the honeycomb core.' },
      { title: 'Same feel on every shot', data: 'Bends evenly from soft to hard shots', copy: 'Predictable response whatever the pace.' },
      { title: 'Deeper-toned pop', data: '785 Hz vs ProFoam at 908 Hz', copy: 'A lower-pitched sound on impact for players who like that feedback.' },
    ],
    stats: [
      { value: '+3.3%', label: 'twist weight', note: 'steadier off-centre' },
      { value: '37%', label: 'less face bend', note: 'on soft shots' },
    ],
  },
  profoam: {
    slug: 'profoam',
    name: 'ProFoam',
    fullName: 'DYSSC ProFoam Pro',
    accent: '#1E6FD9',
    identity: 'Light, quick foam-core feel',
    core: 'Foam',
    summary:
      'Carbon faces over a foam core. ProFoam is lighter and quicker to swing, gives a little more on soft shots and firms up on hard ones.',
    chooseFor: 'A lighter, quicker paddle in a 218.5 g build.',
    highlights: [
      { title: 'Lighter', data: '218.5 g — 13 g lighter than HexCore', copy: 'Less weight in the hand for players who like a lighter paddle.' },
      { title: 'Quicker to swing', data: 'Swing weight 6.4% lower than HexCore', copy: 'Easier to get the paddle around at the kitchen line.' },
      { title: 'Softer touch, firm power', data: 'Gives more on soft shots, firms up on hard ones', copy: 'A feel that changes with how hard you hit.' },
      { title: 'Brighter-toned pop', data: '908 Hz vs HexCore at 785 Hz', copy: 'A higher-pitched sound on impact with its own feedback.' },
    ],
    stats: [
      { value: '218.5', label: 'g weight', note: 'lighter build' },
      { value: '6.4%', label: 'lower swing weight', note: 'quicker to swing' },
    ],
  },
};

// Side-by-side metrics. `better` marks the direction each model leads in — presented as a preference, never a winner.
export const METRICS = [
  { key: 'core', label: 'Core', hexcore: 'Polypropylene honeycomb', profoam: 'Foam', type: 'text' },
  { key: 'weight', label: 'Weight', unit: 'g', hexcore: 231.8, profoam: 218.5, max: 260, leads: 'profoam', leadNote: 'ProFoam is 5.7% lighter', hexNote: 'More substantial build', proNote: 'Lower-weight option' },
  { key: 'balance', label: 'Balance point', unit: 'mm', hexcore: 243.5, profoam: 239.1, max: 300, leads: null, condition: 'Measured from the end of the handle', hexNote: 'A touch more head weight', proNote: 'A touch more hand weight' },
  { key: 'swing', label: 'Swing weight', unit: 'kg cm²', hexcore: 117.6, profoam: 110.1, max: 140, leads: 'profoam', leadNote: 'ProFoam is 6.4% lower', condition: 'How much effort it takes to swing', hexNote: 'Carries more into the ball', proNote: 'Quicker to swing' },
  { key: 'twist', label: 'Twist weight', unit: 'kg cm²', hexcore: 5.67, profoam: 5.49, max: 7, leads: 'hexcore', leadNote: 'HexCore is 3.3% higher', condition: 'How well it resists twisting on off-centre hits', hexNote: 'Steadier on off-centre hits', proNote: 'Twists a little more' },
  { key: 'thickness', label: 'Thickness', unit: 'mm', hexcore: 16, profoam: 16, max: 20, leads: null, hexNote: 'Same 16 mm build', proNote: 'Same 16 mm build' },
  { key: 'shape', label: 'Shape', hexcore: '417 × 187 mm', profoam: '418 × 188 mm', type: 'text', condition: 'Same shape, length × head width' },
  { key: 'bending', label: 'Face bend on soft shots', unit: 'mm/kg', hexcore: 0.38, profoam: 0.61, max: 0.8, leads: 'hexcore', leadNote: 'HexCore bends about 37% less', condition: 'How far the face dips per kg (light loads)', hexNote: 'Firmer feel on touch shots', proNote: 'Softer feel on touch shots' },
  { key: 'sound', label: 'Impact sound', unit: 'Hz', hexcore: 785, profoam: 908, max: 1200, leads: null, condition: 'Lower number = deeper tone (not loudness)', hexNote: 'Deeper-toned pop', proNote: 'Brighter-toned pop' },
  { key: 'reason', label: 'Main reason to choose', hexcore: 'Firmer feel and steadier on off-centre hits', profoam: 'Lighter and quicker to swing', type: 'text' },
];

export const SELECTOR = [
  { pref: 'I like a firmer feel on touch shots.', pick: 'hexcore', reason: 'About 37% less face bend on soft shots.' },
  { pref: 'I want the paddle steady on off-centre hits.', pick: 'hexcore', reason: 'Higher twist weight: 5.67 vs 5.49.' },
  { pref: 'I like a bit more weight in the hand.', pick: 'hexcore', reason: 'The solid 231.8 g build.' },
  { pref: 'I prefer a lighter paddle.', pick: 'profoam', reason: 'The lighter 218.5 g build.' },
  { pref: 'I want quick hands at the kitchen.', pick: 'profoam', reason: 'Swing weight 6.4% lower: 110.1 vs 117.6.' },
  { pref: 'Sound and feedback matter to me.', pick: 'both', reason: 'HexCore has the deeper-toned pop (785 Hz vs 908 Hz). Pick the feedback you prefer.' },
];

export const TECH_FAQ = [
  { q: 'Why choose HexCore?', a: 'Choose HexCore if you like a firmer feel on touch shots, a paddle that stays steady on off-centre hits and a little more weight in the hand. Its honeycomb core bends less on soft shots and responds the same way on every shot.' },
  { q: 'Why choose ProFoam?', a: 'Choose ProFoam if you want a lighter paddle that is quicker to swing. Its foam-core build weighs 218.5 g and has the lower swing weight of the two.' },
  { q: 'Which paddle feels firmer?', a: 'HexCore. On soft shots its face bends about 37% less than ProFoam (0.38 vs 0.61 mm per kg). On hard shots ProFoam firms up and the two feel closer.' },
  { q: 'Which paddle is steadier on off-centre hits?', a: 'HexCore has the higher twist weight (5.67 vs 5.49), so it resists twisting a little more when the ball lands off-centre.' },
  { q: 'What is swing weight?', a: 'How much effort it takes to swing the paddle. ProFoam has the lower swing weight (110.1 vs 117.6), so it is quicker to get around; HexCore carries a little more into the ball.' },
  { q: 'How does the feel change with how hard I hit?', a: 'HexCore bends the same way from soft to hard shots. ProFoam gives more on soft shots and firms up as you hit harder, so the two feel most different on dinks and resets.' },
  { q: 'Do the paddles sound different?', a: 'Yes. HexCore has a deeper-toned pop (785 Hz) and ProFoam a brighter one (908 Hz). This is about tone, not which paddle is louder or quieter.' },
  { q: 'Are they the same size?', a: 'Yes. Both are 16 mm thick and share the same shape — about 417 mm long and 188 mm across the head. Only the core inside is different: honeycomb in HexCore, foam in ProFoam.' },
];

export const DATA_REFERENCE =
  'Source: independent measurement report DYSSC-M04-2026 (October 2026) on reference paddles of each build. Production paddles may vary slightly.';

// ---- Additional measured data from the feasibility report (DYSSC-M04-2026) ----
export const DIMENSIONS = {
  length: { hexcore: 417, profoam: 418, unit: 'mm', label: 'Length' },
  headWidth: { hexcore: 187, profoam: 188, unit: 'mm', label: 'Head width' },
  thickness: { hexcore: 16, profoam: 16, unit: 'mm', label: 'Thickness' },
  balance: { hexcore: 243.5, profoam: 239.1, unit: 'mm', label: 'Balance point' },
  sweetSpotStart: 292,
  consistency: 'Every paddle we measured matched its twin within 1%.',
};

// Where the grams live — parts weighed at the teardown
export const MASS_BREAKDOWN = {
  hexcore: { whole: 231.8, parts: [
    { key: 'body', label: 'Body — carbon faces + honeycomb core', g: 165.4 },
    { key: 'guard', label: 'Edge guard', g: 37.9 },
    { key: 'handle', label: 'Handle & grip', g: 28.4 },
  ] },
  profoam: { whole: 218.5, parts: [
    { key: 'body', label: 'Body — carbon faces + foam core', g: 155.0 },
    { key: 'guard', label: 'Edge guard', g: 38.0 },
    { key: 'handle', label: 'Handle & grip', g: 27.3 },
  ] },
  insight: 'Same edge guard. Same handle. Almost all of the 13 g difference is in the body — the core decides the weight.',
};

// Reference bands published for other paddles (no brands named on the public site)
export const MARKET_BANDS = [
  { key: 'weight', label: 'Weight', unit: 'g', min: 190, max: 245, hexcore: 231.8, profoam: 218.5,
    bands: [{ label: 'Most quality paddles', from: 215.5, to: 232.5 }],
    note: 'Both DYSSC paddles weigh what quality paddles weigh — ProFoam at the light end, HexCore at the solid end.' },
  { key: 'twist', label: 'Twist weight', unit: 'kg cm²', min: 4.5, max: 8, hexcore: 5.67, profoam: 5.49,
    bands: [{ label: 'Imported premium paddles', from: 5.51, to: 7.75 }],
    note: 'HexCore matches imported premium paddles for steadiness on off-centre hits; ProFoam sits just below that range.' },
];

// Figure 4 of the report: where each test was run on the shared planform (mm from the butt → SVG y = 417 − mm)
export const TEST_SITES = [
  { id: 1, x: 100, y: 150, title: 'Bend test', short: 'Bend test', body: 'Weights from 1 to 10 kg were placed on the face and we measured how far it dipped. That gives the “face bend” numbers.' },
  { id: 2, x: 100, y: 92, title: 'Sound test', short: 'Sound test', body: 'A pickleball was dropped from 1.5 m onto the face and the sound was recorded. That gives the impact-sound numbers.' },
  { id: 3, x: 100, y: 367, title: 'Swing & twist', short: 'Swing & twist', body: 'How hard the paddle is to swing, and how much it resists twisting, worked out from the weight of every part and where it sits.' },
  { id: 4, x: 158, y: 42, title: 'Core sample', short: 'Core sample', body: 'Small blocks were cut from each core — honeycomb and foam — and pressed in a lab to see how they compress.' },
  { id: 5, x: 100, y: 173.5, title: 'Weight & balance', short: 'Balance', body: 'Each paddle was weighed whole, then balanced to find its balance point: 243.5 mm (HexCore) and 239.1 mm (ProFoam) from the end of the handle.' },
  { id: 6, x: 152, y: 262, title: 'Edge guard', short: 'Edge guard', body: 'The edge guard was lifted off to check how firmly it is bonded to the paddle.' },
];

export const GLOSSARY = [
  { q: 'Weight', a: 'The whole paddle on the scale. Lighter is quicker to move; heavier carries more into the ball.' },
  { q: 'Balance point', a: 'Where the paddle balances on your finger, measured from the end of the handle. Further up means a more head-heavy feel.' },
  { q: 'Swing weight', a: 'How much effort it takes to swing the paddle. Lower means quicker hands.' },
  { q: 'Twist weight', a: 'How well the paddle resists twisting when the ball lands off-centre. Higher means steadier.' },
  { q: 'Face bend', a: 'How far the face dips under a known weight. Lower means a firmer feel.' },
  { q: 'Impact sound (Hz)', a: 'The tone of the pop when the ball hits. A lower number is a deeper tone — it is not a measure of loudness.' },
];

export const TEST_METHOD_NOTE =
  'Every number here was measured on real paddles — weighed, balanced, bent under known weights and struck by a dropped ball.';

// Raw photographs from the measurement programme (teardown, rules, calipers, cores, edge guards)
export const GALLERY = [
  { src: `${API}/static/technicals/gallery/g01.webp`, w: 774, h: 1000, caption: 'Both paddles taken apart for the teardown' },
  { src: `${API}/static/technicals/gallery/g02.webp`, w: 750, h: 1000, caption: 'HexCore: every part laid out for weighing' },
  { src: `${API}/static/technicals/gallery/g03.webp`, w: 750, h: 1000, caption: 'ProFoam: every part laid out for weighing' },
  { src: `${API}/static/technicals/gallery/g04.webp`, w: 750, h: 1000, caption: 'Sectioned units of both builds, side by side' },
  { src: `${API}/static/technicals/gallery/g13.webp`, w: 750, h: 1000, caption: 'HexCore honeycomb core block, sawn for the coupon' },
  { src: `${API}/static/technicals/gallery/g14.webp`, w: 750, h: 1000, caption: 'ProFoam foam core block, sawn for the coupon' },
  { src: `${API}/static/technicals/gallery/g15.webp`, w: 808, h: 689, caption: 'Honeycomb cells beneath the carbon face sheet' },
  { src: `${API}/static/technicals/gallery/g16.webp`, w: 640, h: 425, caption: 'Carbon face weave under the microscope' },
  { src: `${API}/static/technicals/gallery/g17.webp`, w: 640, h: 425, caption: 'Carbon face weave, second view' },
];

// Plain-language 1–5 play profile shown on product pages
export const PLAY_PROFILE = {
  hexcore: {
    bestFor: 'Players who want a solid, steady paddle with a firm, controlled feel.',
    ratings: [
      { label: 'Steadiness', score: 5, note: 'Stays steady when you miss the sweet spot' },
      { label: 'Firm feel', score: 5, note: 'Solid, controlled touch on dinks and resets' },
      { label: 'Hand speed', score: 3, note: 'Balanced swing with weight behind the ball' },
      { label: 'Lightness', score: 3, note: 'A solid, substantial build' },
    ],
  },
  profoam: {
    bestFor: 'Players who want a light, fast paddle with a softer touch.',
    ratings: [
      { label: 'Steadiness', score: 4, note: 'Steady, with a slightly livelier face' },
      { label: 'Firm feel', score: 3, note: 'Softer on dinks, firms up when you drive' },
      { label: 'Hand speed', score: 5, note: 'Quick to react at the kitchen line' },
      { label: 'Lightness', score: 5, note: 'Light in the hand, easy over long games' },
    ],
  },
};
